import { Component, OnInit, Input, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { AppConfigService } from '../../../app-config.service';
import { AdresseModele } from '../../../propriete/modeles/proprieteModel';
import { AddressUnit } from '../../modeles/address-unit.modele';

declare var pca: any;

@Component({
  selector: 'app-address-input',
  templateUrl: './address-input.component.html',
  styleUrls: ['./address-input.component.scss'],
  standalone: false
})
export class AddressInputComponent implements OnInit, AfterViewInit {
  @Input()
  parentFormGroup: FormGroup;

  @Input()
  activePosteCanada: boolean;

  @Input()
  id: string;

  @Input()
  addressComplete: AdresseModele;

  @Input()
  placeholder: string;

  @Input()
  provinces: Array<string> = [];

  @Input()
  formName: string;

  @Output()
  valueChange: EventEmitter<AdresseModele> = new EventEmitter<AdresseModele>();

  @Input()
  accessibilityLabel: string;

  @Input()
  required = false;

  @Input()
  isDSD = false;

  @Input()
  label: string;

  addressFormControl: AbstractControl;

  private selectedAddress: AdresseModele = {
    adresseFormattee: null,
    codePostal: null,
    numeroCivique: null,
    pays: null,
    rue: null,
    territoire: null,
    typeUnite: null,
    unite: null,
    ville: null
  };

  constructor(private readonly _appConfigService: AppConfigService) {}

  ngOnInit() {
    this.addressFormControl = this.parentFormGroup.get(this.formName);
    if (this.addressComplete) {
      this.setAddress(this.addressComplete);
    }
  }

  ngAfterViewInit() {
    if (this.activePosteCanada) {
      setTimeout(() => {
        this.initPosteCanada(this.id);
      }, 250);
    }
    this.configureValidators();
    this.preventErrorFlicker();
  }

  private initPosteCanada(id: string): void {
    if (!pca) {
      this.activePosteCanada = false;
    }

    const fields = [{ element: id, field: 'Label', mode: pca.fieldMode.DEFAULT }];
    const options = {
      key: 'EA76-ER97-UC94-YP13',
      languagePreference: this._appConfigService.configData.contexteEnvironnement.langue === 'fr' ? 'fra' : 'eng',
      culture: this._appConfigService.configData.contexteEnvironnement.langue === 'fr' ? 'fr-ca' : 'en-ca',
      setCountryByIP: false,
      bar: {
        showCountry: false
      }
    };
    const control = new pca.Address(fields, options);

    control.listen('ready', function() {
      control.setCountry('Canada');
    });

    if (this.isDSD) {
      control.listen('blur', () => this.preventDSDErrorFlicker());
    }

    control.listen('populate', address => {
      this.selectedAddress.adresseFormattee = address.Label.replace(/\n/gi, ', ');
      this.selectedAddress.rue = address.Street;
      this.selectedAddress.codePostal = address.PostalCode.replace(' ', '') || '';
      this.selectedAddress.numeroCivique = address.BuildingNumber;
      this.selectedAddress.pays = address.CountryIso2;
      this.selectedAddress.territoire = address.ProvinceCode;
      this.selectedAddress.typeUnite = this.getTypeUnite({ type: address.Type, unit: address.SubBuilding });
      this.selectedAddress.unite = address.SubBuilding;
      this.selectedAddress.ville = address.City;
      this.valueChange.emit(this.selectedAddress);
      this.addressFormControl.setValue(this.selectedAddress.adresseFormattee);
    });
  }

  private preventDSDErrorFlicker() {
    setTimeout(() => {
      this.addressFormControl.updateValueAndValidity();
    }, 150);
  }

  get textAreaHeight() {
    const element = document.querySelector('#' + this.id);
    return element ? element.scrollHeight : 0;
  }

  markAsPending() {
    this.addressFormControl.markAsPending();
  }

  setAddress(address: AdresseModele) {
    this.selectedAddress = address;
    this.addressFormControl.setValue(address.adresseFormattee);
  }

  updateSelectedAddress(event): void {
    // The address must come from the Canada Post autocomplete
    if (event.target.value !== this.selectedAddress.adresseFormattee) {
      this.resetSelectedAddress();
    }
  }

  private getTypeUnite(addressUnit: AddressUnit) {
    const type = addressUnit.type;

    if (type === 'Residential') {
      return 'APP';
    } else if (type === 'Commercial') {
      return 'UNITE';
    }

    return '';
  }

  get textAreaValue() {
    const element = document.querySelector(`#${this.id}`) as HTMLTextAreaElement;
    return element ? element.value : '';
  }

  private configureValidators() {
    const validators = [];

    if (this.provinces.length > 0) {
      validators.push(this.valideProvince());
    }

    const validator = this.addressFormControl.validator({} as AbstractControl);
    if (validator && validator.required) {
      validators.push(Validators.required);
    }

    validators.push(this.validAdressValidator());

    this.addressFormControl.setValidators(validators);
  }

  private valideProvince() {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        const isWithinProvinces =
          !this.selectedAddress.territoire || this.provinces.indexOf(this.selectedAddress.territoire) > -1;
        return isWithinProvinces ? null : { outsideProvinces: { value: control.value } };
      }
    };
  }

  private validAdressValidator() {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isValidAddress = !control.value || this.selectedAddress.adresseFormattee;
      return isValidAddress ? null : { invalid: { value: control.value } };
    };
  }

  private preventErrorFlicker(): void {
    // Fix for mat-error flicker on address selection with Canada Post autocomplete
    const pcaElements = typeof document !== 'undefined' ? document.getElementsByClassName('pca') : [];
    if (pcaElements.length > 0) {
      pcaElements[0].addEventListener('click', () => {
        this.addressFormControl.markAsPristine();
        this.addressFormControl.markAsUntouched();
      });
    }
  }

  private resetSelectedAddress(): void {
    Object.keys(this.selectedAddress).forEach(key => (this.selectedAddress[key] = null));
  }
}
